import { OperationVariables } from "@apollo/client";
import {
  Button,
  DisplayOn,
  FileCsvIcon,
  IconButton,
  Tooltip,
} from "@songtradr-one/components-frontend";
import { ExportButtonProps, text, useCsvExport } from "./ExportButton.Parts";

/**
 * A component used to export GraphQL data from a table to a CSV file
 *
 * This component uses the useToast hook to display error messages and therefore must be used within a ToastProvider.
 *
 * @param query - The GraphQL query to fetch the data to be exported.
 * @param select - Picks the rows out of the response, e.g. (data) => data.channels.connected.items.
 * @param disabled - A boolean indicating whether the export button should be disabled.
 * @param fileName - The name of the file to be downloaded. If not provided, it defaults to "exported_data".
 * @param subObjectFields - An optional array of paths to properties within the subobject fields of the GraphQL query.
 *                          This is used to extract and output the value of a field that resides inside a nested subobject.
 *                          For example, if a query has a `user` field that is equal to a subobject that contains a `name` field,
 *                          one can set subObjectFields equal to `["user.name"]` to output the `name` field's value from the `user` field's subobject.
 *                          If the `user` subobject has a field called `names` that is also a subobject, and the `names` subobject has a field called lastName,
 *                          one can set subObjectFields equal to `["user.names.lastName"]` to output the `lastName` field's value from the `user` subobject's `names` subobject.
 *                          This allows for deep extraction of values from nested subobjects.
 * @param excludeFields - An optional array of field names to exclude from the exported data.
 *                        These fields will be omitted from the resulting CSV file.
 *                        By default, the `__typename` field is excluded, as it is typically not needed in exports.
 *                        You can specify additional fields to exclude by providing their names in the array.
 *                        For example, to exclude `updatedAt` and `createdBy`, you can pass `["updatedAt", "createdBy"]`.
 * @param columnLabels - Optional map of field key to CSV header text. Fields without a label keep their key as header.
 * @param fetchPolicy - Optional Apollo fetch policy. Pass "network-only" when the file must match the server.
 * @param appearance - "responsive" (default) is a filled button on desktop and an icon on mobile; "icon" is an icon at both sizes.
 */
export const ExportButton = <
  TData,
  TItem extends Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
>({
  disabled = false,
  appearance = "responsive",
  ...options
}: ExportButtonProps<TData, TItem, TVariables>) => {
  const { exportCsv, loading } = useCsvExport(options);

  const icon = (
    <Tooltip tip={text.tip}>
      <IconButton
        onClick={exportCsv}
        disabled={disabled || loading}
        aria-label={text.tip}
      >
        <FileCsvIcon />
      </IconButton>
    </Tooltip>
  );

  if (appearance === "icon") return icon;

  return (
    <>
      <DisplayOn mobile>{icon}</DisplayOn>

      <DisplayOn desktop>
        <Button
          $variant="important"
          onClick={exportCsv}
          disabled={disabled}
          loading={loading}
        >
          {text.label}
        </Button>
      </DisplayOn>
    </>
  );
};
